import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode, RefObject } from 'react'

/**
 * The overlay scrollbar — a custom thumb drawn OVER the content (no layout width reserved),
 * replacing the native bar. Self-contained: pure React, no third-party lib, scoped to the one
 * scroll it tracks. Colours are token-driven.
 *
 * COPIED FROM MONZA (monzadev `packages/ui/src/components/OverlayScrollbar.tsx`) per the
 * copy-not-share rule, renamed on the way in. Deliberately NOT the viewer's own
 * `apps/web/src/components/OverlayScrollbar.tsx`: that one reveals on hovering the BAR (or a
 * `parentHovered` region a caller has to remember to pass), which is a poster-rail behaviour.
 * This one reveals on entering the SCROLLED REGION, which is what David specified for the
 * portals — and it needs no cooperation from the caller.
 *
 * Visibility (David's spec):
 *   - mouse NOT in the scroll region -> fully hidden.
 *   - mouse IN the region            -> thumb at ~40% (a faint affordance).
 *   - mouse ON the scrollbar         -> thumb darkens AND the track shows a faint channel tint
 *                                       (so you can see where clicking page-scrolls).
 *   - dragging                       -> darkest. Counts as "on".
 *
 * Mobile (touch, no hover) gets NO overlay — native momentum scrolling only.
 */
const MIN_THUMB = 28
const IDLE_MS = 1000
// Intensity ramp: faint in-region -> darker on bar hover -> darkest on press/drag.
const PARTIAL_OPACITY = 0.4
const HOVER_OPACITY = 0.7
const DRAG_OPACITY = 1
// Hit-area thickness of the track (hover to reveal; click to page-scroll).
const TRACK_HIT = 14
// Distance from the panel's edge to the bar. 0 = flush: the scrollbar belongs to the edge of the
// region it scrolls, so it reads as chrome rather than as content floating above it. The 14px
// hit-area still extends inward, so the target stays easy to grab.
const EDGE_GAP = 0
const THUMB_COLOR = 'var(--overlay-scrollbar-thumb, #5b5b5b)'
const TRACK_COLOR = 'var(--overlay-scrollbar-track, rgba(0,0,0,0.07))'

function isMobileDevice(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none) and (pointer: coarse)').matches
}

interface Props {
  axis?: 'x' | 'y'
  /** Element to track; omit to track the window/document scroll. */
  scrollRef?: RefObject<HTMLElement | null>
  /** Thumb positioning context. 'fixed' = viewport; 'absolute' = positioned ancestor. */
  position?: 'fixed' | 'absolute'
  /** px reserved at the start of the track (e.g. a fixed header). */
  insetStart?: number
  thickness?: number
}

export function OverlayScrollbar({ axis = 'y', scrollRef, position = 'fixed', insetStart = 0, thickness = 8 }: Props) {
  const [mobile] = useState(isMobileDevice)
  const [m, setM] = useState({ thumb: 0, pos: 0, scrollable: false })
  const [scrolling, setScrolling] = useState(false)
  const [regionHovered, setRegionHovered] = useState(false)
  const [barHovered, setBarHovered] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const idleTimer = useRef<number | undefined>(undefined)
  const dragging = useRef(false)
  const isX = axis === 'x'

  const read = useCallback(() => {
    const el = scrollRef?.current
    if (el) {
      return isX
        ? { size: el.clientWidth, scrollSize: el.scrollWidth, pos: el.scrollLeft }
        : { size: el.clientHeight, scrollSize: el.scrollHeight, pos: el.scrollTop }
    }
    return isX
      ? { size: window.innerWidth, scrollSize: document.documentElement.scrollWidth, pos: window.scrollX }
      : { size: window.innerHeight, scrollSize: document.documentElement.scrollHeight, pos: window.scrollY }
  }, [isX, scrollRef])

  const recompute = useCallback(() => {
    const { size, scrollSize, pos } = read()
    const scrollable = scrollSize > size + 1
    const track = Math.max(size - insetStart, 0)
    const thumb = scrollable ? Math.max((size / scrollSize) * track, MIN_THUMB) : 0
    const maxThumb = track - thumb
    const thumbPos = scrollable && scrollSize > size ? insetStart + (pos / (scrollSize - size)) * maxThumb : insetStart
    setM({ thumb, pos: thumbPos, scrollable })
  }, [read, insetStart])

  const flash = useCallback(() => {
    setScrolling(true)
    window.clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => {
      if (!dragging.current) setScrolling(false)
    }, IDLE_MS)
  }, [])

  useEffect(() => {
    if (mobile) return
    recompute()
    // Content often lays out AFTER mount (async data, fonts), so the mount-time measure can read
    // "not scrollable". Re-measure on the next frame and shortly after so the thumb is ready
    // before the first interaction — not only after the first wheel.
    const raf = requestAnimationFrame(recompute)
    const late = window.setTimeout(recompute, 250)
    const el = scrollRef?.current
    const target: Window | HTMLElement = el ?? window
    const onScroll = () => {
      recompute()
      flash()
    }
    target.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', recompute)
    const ro = new ResizeObserver(recompute)
    ro.observe(el ?? document.body)
    if (el?.firstElementChild) ro.observe(el.firstElementChild)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(late)
      target.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', recompute)
      ro.disconnect()
    }
  }, [mobile, recompute, flash, scrollRef])

  // "Mouse in the scroll region" drives the 40% affordance. Track it on the scrolled element
  // itself (window-tracked scrollers count as always in-region).
  useEffect(() => {
    if (mobile) return
    const el = scrollRef?.current
    if (!el) {
      setRegionHovered(true)
      return
    }
    // Re-measure on enter too — if the mount-time measure missed (content not yet laid out),
    // drifting in now finds it scrollable so the thumb shows without a wheel first.
    const enter = () => {
      recompute()
      setRegionHovered(true)
    }
    const leave = () => setRegionHovered(false)
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    return () => {
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
    }
  }, [mobile, scrollRef, recompute])

  const onDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    setDragActive(true)
    setScrolling(true)
    const start = isX ? e.clientX : e.clientY
    const r0 = read()
    const startScroll = r0.pos
    const track = Math.max(r0.size - insetStart, 0)
    const maxThumb = track - m.thumb
    const ratio = maxThumb > 0 ? (r0.scrollSize - r0.size) / maxThumb : 0
    const move = (ev: MouseEvent) => {
      const next = startScroll + ((isX ? ev.clientX : ev.clientY) - start) * ratio
      const el = scrollRef?.current
      if (el) {
        if (isX) el.scrollLeft = next
        else el.scrollTop = next
      } else {
        window.scrollTo(isX ? { left: next } : { top: next })
      }
    }
    const up = () => {
      dragging.current = false
      setDragActive(false)
      flash()
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  // Click the track (not the thumb) -> page-scroll toward the click, resolved in the same
  // coordinate space as the thumb position `m.pos`.
  const onTrackDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    const { size, scrollSize, pos } = read()
    if (scrollSize <= size) return
    const el = scrollRef?.current
    const rect = el?.getBoundingClientRect()
    const pointer = isX ? e.clientX - (rect?.left ?? 0) : e.clientY - (rect?.top ?? 0)
    const dir = pointer < m.pos ? -1 : 1
    const next = Math.max(0, Math.min(pos + dir * size * 0.9, scrollSize - size))
    const opts = (isX ? { left: next } : { top: next }) as ScrollToOptions
    opts.behavior = 'smooth'
    if (el) el.scrollTo(opts)
    else window.scrollTo(opts)
  }

  if (mobile || !m.scrollable) return null

  // Three-tier intensity: press/drag darkest, bar-hover darker, in-region faint.
  const active = barHovered || dragActive
  const opacity = dragActive ? DRAG_OPACITY : barHovered ? HOVER_OPACITY : regionHovered || scrolling ? PARTIAL_OPACITY : 0

  const trackStyle: CSSProperties = {
    position,
    zIndex: 60,
    touchAction: 'none',
    ...(isX
      ? { left: insetStart, right: 0, bottom: 0, height: TRACK_HIT }
      : { top: insetStart, bottom: 0, right: 0, width: TRACK_HIT }),
  }

  // The channel behind the thumb — faintly tinted only while the bar is hovered, so the user
  // sees where a track-click will page-scroll. Flush to the panel edge (EDGE_GAP 0): the bar
  // reads as part of the panel's own edge, not as a floating strip inset from it.
  const railStyle: CSSProperties = {
    position: 'absolute',
    borderRadius: 6,
    background: active ? TRACK_COLOR : 'transparent',
    transition: 'background 200ms',
    pointerEvents: 'none',
    ...(isX ? { left: 0, right: 0, bottom: EDGE_GAP, height: thickness } : { top: 0, bottom: 0, right: EDGE_GAP, width: thickness }),
  }

  const thumbStyle: CSSProperties = {
    position: 'absolute',
    borderRadius: 6,
    background: THUMB_COLOR,
    opacity,
    transition: 'opacity 200ms',
    touchAction: 'none',
    ...(isX
      ? { bottom: EDGE_GAP, left: m.pos - insetStart, width: m.thumb, height: thickness }
      : { right: EDGE_GAP, top: m.pos - insetStart, height: m.thumb, width: thickness }),
  }

  return (
    <div
      onMouseEnter={() => setBarHovered(true)}
      onMouseLeave={() => setBarHovered(false)}
      onMouseDown={onTrackDown}
      aria-hidden
      style={trackStyle}
    >
      <div style={railStyle} />
      <div onMouseDown={onDown} style={thumbStyle} />
    </div>
  )
}

interface AreaProps {
  children: ReactNode
  /** Which axes scroll. Default 'y'. */
  axis?: 'x' | 'y' | 'both'
  className?: string
  style?: CSSProperties
  thickness?: number
}

/**
 * Convenience wrapper: a scroll container with the native bar hidden and the overlay thumb(s)
 * mounted. Drop-in for an `overflow:auto` region — fills its parent (height/width 100%), so give
 * the PARENT a bounded size.
 */
export function OverlayScrollArea({ children, axis = 'y', className, style, thickness }: AreaProps) {
  const ref = useRef<HTMLDivElement>(null)
  const overflow = axis === 'x' ? 'auto hidden' : axis === 'both' ? 'auto' : 'hidden auto'
  return (
    <div
      style={{ position: 'relative', height: '100%', width: '100%', minHeight: 0, minWidth: 0, ...style }}
      className={className}
    >
      <div ref={ref} className="yoreflix-no-scrollbar" style={{ height: '100%', width: '100%', overflow }}>
        {children}
      </div>
      {(axis === 'y' || axis === 'both') && <OverlayScrollbar axis="y" scrollRef={ref} position="absolute" thickness={thickness} />}
      {(axis === 'x' || axis === 'both') && <OverlayScrollbar axis="x" scrollRef={ref} position="absolute" thickness={thickness} />}
    </div>
  )
}
